/**
 * KAN-773, KAN-790: строка редактора лестницы касаний - номер, этап, задержка,
 * что делаем (канал шага), поля шага по признакам канала (рычаг, текст,
 * срок), удаление.
 *
 * Колонки "Этап" и "Что делаем" есть только когда схема поля отдала каналы
 * шага (`staged`); без них строка выглядит как до KAN-790.
 */
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { NativeSelect } from '@/components/ui/native-select';
import {
  channelNeeds,
  stepChannel,
  stepStage,
  type NudgeChannelOption,
  type NudgeLadderStep,
  type NudgeLeverOption,
} from '@/lib/nudgeLadder';
import { HoursField } from './HoursField';
import { NudgeTaskFields } from './NudgeTaskFields';

const ANY_STAGE: NudgeLeverOption = { value: '', label: 'Любой' };

interface NudgeLadderRowProps {
  idPrefix: string;
  testId?: string;
  index: number;
  step: NudgeLadderStep;
  /** Схема отдала каналы шага: показывать колонки этапа и канала. */
  staged: boolean;
  leverOptions: ReadonlyArray<NudgeLeverOption>;
  channelOptions: ReadonlyArray<NudgeChannelOption>;
  stageOptions: ReadonlyArray<NudgeLeverOption>;
  onChange: (patch: Partial<NudgeLadderStep>) => void;
  onRemove: () => void;
  disabled?: boolean;
}

/** Options plus the stored value when the list does not have it (stays selectable). */
function withCurrent(options: ReadonlyArray<NudgeLeverOption>, current: string): NudgeLeverOption[] {
  const plain = options.map(({ value, label }) => ({ value, label }));
  if (current === '' || plain.some((o) => o.value === current)) return plain;
  return [...plain, { value: current, label: current }];
}

export function NudgeLadderRow({
  idPrefix,
  testId,
  index,
  step,
  staged,
  leverOptions,
  channelOptions,
  stageOptions,
  onChange,
  onRemove,
  disabled = false,
}: NudgeLadderRowProps): JSX.Element {
  const n = index + 1;
  const tid = (name: string): string | undefined => (testId ? `${testId}-${name}-${index}` : undefined);
  const stage = stepStage(step);
  const channel = stepChannel(step);
  const needs = channelNeeds(channel, channelOptions);

  return (
    <tr data-testid={tid('row')}>
      <td className="py-1 pr-2 align-top leading-10">{n}</td>
      {staged && (
        <td className="py-1 pr-2 align-top">
          <NativeSelect
            id={`${idPrefix}-stage-${index}`}
            data-testid={tid('stage')}
            aria-label={`Этап касания ${n}`}
            className="min-w-[8rem]"
            options={[ANY_STAGE, ...withCurrent(stageOptions, stage)]}
            value={stage}
            disabled={disabled}
            onChange={(e) => onChange({ stage: e.target.value })}
          />
        </td>
      )}
      <td className="py-1 pr-2 align-top">
        <HoursField
          id={`${idPrefix}-delay-${index}`}
          unitId={`${idPrefix}-unit-${index}`}
          testId={tid('delay')}
          unitTestId={tid('unit')}
          label={`Задержка касания ${n}`}
          unitLabel={`Единица задержки касания ${n}`}
          hours={step.delay_h}
          onChange={(hours) => onChange({ delay_h: hours })}
          disabled={disabled}
        />
      </td>
      {staged && (
        <td className="py-1 pr-2 align-top">
          <NativeSelect
            id={`${idPrefix}-channel-${index}`}
            data-testid={tid('channel')}
            aria-label={`Что делаем на касании ${n}`}
            className="min-w-[10rem]"
            options={withCurrent(channelOptions, channel)}
            value={channel}
            disabled={disabled}
            onChange={(e) => onChange({ channel: e.target.value })}
          />
        </td>
      )}
      <td className="py-1 pr-2 align-top">
        <div className="space-y-1">
          {needs.lever && (
            <NativeSelect
              id={`${idPrefix}-lever-${index}`}
              data-testid={tid('lever')}
              aria-label={`Рычаг касания ${n}`}
              options={[...leverOptions]}
              value={step.lever}
              disabled={disabled}
              onChange={(e) => onChange({ lever: e.target.value })}
            />
          )}
          {(needs.text || needs.due) && (
            <NudgeTaskFields
              idPrefix={idPrefix}
              testId={testId}
              number={n}
              index={index}
              step={step}
              showText={needs.text}
              showDue={needs.due}
              onChange={onChange}
              disabled={disabled}
            />
          )}
        </div>
      </td>
      <td className="py-1 align-top">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          data-testid={tid('remove')}
          aria-label={`Удалить касание ${n}`}
          disabled={disabled}
          onClick={onRemove}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </td>
    </tr>
  );
}
